import { Injectable } from '@angular/core';
import { OrderDTO, QuoteDTO, TripDTO } from '../../../../interfaces/models';
import { OrderService } from '../../../../providers/order/order.service';

import * as dayjs from 'dayjs';
import * as deepmerge from 'deepmerge';
import { TableActionService } from './table-action.service';
import { GeneralService } from '../../../../providers/general/general.service';
import { DateService } from '../../../../providers/date/date.service';
import { UserService } from '../../../../providers/user/user.service';
import { PaymentMethodEnum } from '../../../enums/payment-method.enum';
import { TableNavigationService } from './table-navigation.service';
import { ORDER_STATUS, ROLES_ENUM, TRIP_STATUS } from '../../../../enums';
import { CLIENT_PAYMENT_STATUSES, WEB_NOTIFICATION } from '../../../../constants';
import { TableConfiguration } from '../models/table-config.model';
import { INotificatedEntity } from '../../../interfaces/notificated-entity.interface';

type TableViewMapping = Record<string, unknown>;

@Injectable()
export class TableDataMapperService {

  constructor(
    private readonly orderService: OrderService,
    private readonly generalService: GeneralService,
    private readonly dateService: DateService,
    private readonly userService: UserService,
    private readonly tableNavigationService: TableNavigationService,
  ) {
  }

  public getQuoteToViewMapper(quote: QuoteDTO, extendMappings: TableViewMapping = {}) {
    const profit = this.orderService.formatPrice((quote.loadPrice || quote.priceWithDiscount) - quote.salePrice);
    const isAdmin = this.userService.isUserHasRole([ROLES_ENUM.SUPER_ADMIN]);
    const distanse = this.orderService.formatDistance(quote.distance);

    const suggestedPrice = this.getSuggestedPrice(quote);

    const offerAction = this.getOfferAction(quote);

    let record: TableViewMapping = {
      firstRow: {
        identifier: {
          uuid: quote.id,
        },
        markers: {
          distance: distanse,
          trailer: quote.trailerType,
          profit: profit,
          config: {
            distance: { config: { isDisplayed: !!distanse } },
            trailer: { config: { isDisplayed: !!quote.trailerType } },
            profit: { config: { isDisplayed: isAdmin && !!profit && profit > 0 } },
            serviceFee: { config: { isDisplayed: false } }
          }
        },
        actionMenu: {
          model: quote,
          isVertical: true,
          config: {
            sendInvitation: {
              isDisplayed: true,
              header: 'Send Offer',
              click: (quote, service: TableActionService) => service.sentEmail(quote.id),
            },
            offerDiscount: {
              click: offerAction,
              isDisplayed: true,
            },
            addNote: {
              click: (_, service: TableActionService) => service.addQuoteNote(quote.id),
              isDisplayed: true,
            },
            delete: {
              click: (quote, service: TableActionService) => service.removeQuoteCompletely(quote.id),
              isDisplayed: true,
            },
          },
        },
      },
      secondRow: {
        origin: { location: this.orderService.fullAddress(quote.pickLocation) },
        destination: { location: this.orderService.fullAddress(quote.deliveryLocation) },
        cargo: { cars: quote.cars.map(car => ({ ...car, description: `${car.year} ${car.make} ${car.model} ${car.type}` })) },
        client: { email: quote.customer && quote.customer.email || undefined },
        orderDetails: {
          suggestedPrice: suggestedPrice && this.orderService.formatPrice(suggestedPrice) || undefined,
          price: this.orderService.formatPrice(quote.initialPrice),
          // orderStatus: ORDER_STATUS.QUOTE,
        }
      }
    };

    record = this.addQuoteViewSettings(record, quote);

    const result = deepmerge(record, extendMappings);
    return result;
  }

  public getQuotedToViewMapper(quote: QuoteDTO, extendMappings: TableViewMapping = {}) {
    const {
      deliveryDate,
      pickDate,
    } = quote;

    const offerAction = this.getOfferAction(quote);

    const mappings = deepmerge({
      firstRow: {
        actionMenu: {
          config: {
            offerDiscount: {
              click: offerAction,
              isDisplayed: true,
            },
            sendInvitation: {
              isDisplayed: false,
            },
            addNote: {
              isDisplayed: false,
            },
          },
        }
      },
      secondRow: {
        origin: {
          date: this.dateService.toFormatUtcDate(pickDate),
        },
        destination: {
          date: this.dateService.toFormatUtcDate(deliveryDate),
        },
        client: {
          personName: `${quote.createdBy.firstName} ${quote.createdBy.lastName}`
        }
      }
    }, extendMappings)
    return this.getQuoteToViewMapper(quote, mappings);
  }

  public getLoadBoardOrderToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    const {
      dispatches,
    } = order;

    const dispatch = dispatches && dispatches.length ? dispatches[0] : undefined;
    const pickDate = dispatch && dispatch.pickDate || order.pickDate || new Date();
    const deliveryDate = dispatch && dispatch.pickDate || order.deliveryDate || new Date();

    const mappings = deepmerge({
      firstRow: {
        actions: {
          model: order,
          config: {
            bookOrder: {
              config: {
                isDisplayed: true,
              }
            }
          }
        }
      },
      secondRow: {
        selectRowClick: undefined,
        origin: {
          date: this.dateService.toFormatUtcDate(pickDate),
          config: {
            date: {
              config: {
                isLate: order.isQuoteOffered,
                withTooltip: order.isQuoteOffered,
                withTooltipText: "The pickup date was accepted by client",
              },
            },
          },
        },
        destination: {
          date: this.dateService.toFormatUtcDate(deliveryDate),
          config: {
            date: {
              config: {
                isLate: order.isQuoteOffered,
                withTooltip: order.isQuoteOffered,
                withTooltipText: "The delivery date was accepted by client",
              },
            },
          },
        },
      }
    }, extendMappings)
    return this.getOrderToViewMapper(order, mappings);
  }

  public getLoadBoardRequestedOrderToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    const mappings = deepmerge({
      firstRow: {
        actions: {
          config: {
            suggestTerms: {
              config: {
                isDisplayed: true,
                click: (_, tableActionService: TableActionService) => tableActionService.requestDifferentTerms(order),
              }
            },
            cancelOrder: {
              config: {
                isDisplayed: true,
                click: (_, tableActionService: TableActionService) => tableActionService.cancelRequestedOrder(order),
              }
            }
          }
        }
      },
      secondRow: {
        selectRowClick: undefined,
      }
    }, extendMappings);
    return this.getOrderToViewMapper(order, mappings);
  }

  public getOrderToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    const sender = this.normalizeContact(order.sender);
    const receiver = this.normalizeContact(order.receiver);
    const pickLocation = this.normalizeLocation(order.pickLocation);
    const deliveryLocation = this.normalizeLocation(order.deliveryLocation);
    const shipper = this.normalizeShipper(order.shipper);
    const cars = this.normalizeCars(order.cars);
    const salePrice = order.salePrice;
    const distance = order.distance;
    const paymentMethod = this.normalizeOptionalString(order.paymentMethods);

    const profit = this.orderService.formatPrice((order.loadPrice || order.priceWithDiscount) - order.salePrice);
    const isAdmin = this.userService.isUserHasRole([ROLES_ENUM.SUPER_ADMIN]);

    const pickDate = order.pickDate || new Date();
    const deliveryDate = order.deliveryDate || new Date();
    const senderName = [sender.firstName, sender.lastName].filter(Boolean).join(' ');
    const receiverName = [receiver.firstName, receiver.lastName].filter(Boolean).join(' ');
    const shipperLocation = [shipper.state, shipper.city, shipper.zipCode].filter(Boolean).join(', ');
    const hasClientDetails = Boolean(
      shipper.companyName || shipper.fullName || shipperLocation || shipper.email,
    );

    const distanceFormatted = this.orderService.formatDistance(distance);
    const serviceFee = this.getServiceFee(paymentMethod, order);

    const record = {
      firstRow: {
        identifier: {
          uuid: order.externalId || order.uuid,
        },
        markers: {
          distance: distanceFormatted,
          trailer: order.trailerType,
          serviceFee,
          profit,
          paymentMethod,
          config: {
            distance: { config: { isDisplayed: !!distanceFormatted } },
            trailer: { config: { isDisplayed: !!order.trailerType } },
            profit: { config: { isDisplayed: isAdmin && !!profit && profit > 0 } },
            serviceFee: { config: { isDisplayed: !!serviceFee } }
          }
        },
      },
      secondRow: {
        selectRowClick: () => this.tableNavigationService.navigateToOrder(order),
        origin: {
          personName: senderName,
          location: this.orderService.fullAddress(pickLocation),
          phone: sender.phoneNumber,
          date: this.dateService.toFormatUtcDate(pickDate),
          config: {
            date: {
              config: {
                isDisplayed: !!pickDate,
                isLate: this.isLate(pickDate) || order.isQuoteOffered,
                withTooltip: order.isQuoteOffered,
                withTooltipText: "The pickup date was accepted by client",
              },
            },
          },
        },
        destination: {
          personName: receiverName,
          location: this.orderService.fullAddress(deliveryLocation),
          phone: receiver.phoneNumber,
          date: this.dateService.toFormatUtcDate(deliveryDate),
          config: {
            date: {
              config: {
                isDisplayed: !!deliveryDate,
                isLate: this.isLate(pickDate) || order.isQuoteOffered,
                withTooltip: order.isQuoteOffered,
                withTooltipText: "The delivery date was accepted by client",
              },
            },
          },
        },
        cargo: { cars: cars.map(car => ({ ...car, description: `${car.year} ${car.make} ${car.model} ${car.type}`.trim() })) },
        client: hasClientDetails ? {
          businessName: shipper.companyName,
          personName: shipper.fullName,
          location: shipperLocation,
          email: shipper.email || undefined,
        } : {},
        orderDetails: {
          paymentMethod: paymentMethod || undefined,
          price: salePrice && this.orderService.formatPrice(salePrice) || '',
          orderStatus: order.status,
        }
      }
    };

    const result = deepmerge(record, extendMappings);
    return result;
  }

  public getNewLoadOrderToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    const dispatches = order.dispatches || [];
    const firstDispatch = dispatches.length > 0 && dispatches[0] || undefined;
    const {
      dispatcher: dispatcherWhoImportedOrder,
    } = order;

    const dispatcher = dispatcherWhoImportedOrder || firstDispatch && firstDispatch.account;
    const isCarrier = this.userService.isUserHasRole([ROLES_ENUM.COMPANY_ADMIN]);

    const mappings = deepmerge({
      firstRow: {
        executers: {
          dispatcher: isCarrier && dispatcher && {
            firstName: dispatcher && dispatcher.firstName || '',
            lastName: dispatcher && dispatcher.lastName || '',
            avatarUrl: dispatcher && dispatcher.avatarUrl || '',
          } || undefined,
        },
        actionMenu: {
          isVertical: true,
          config: {
            createTrip: {
              isDisplayed: true,
              click: (_, service: TableActionService) => service.createTrip(order),
            },
            addToTrip: {
              click: (_, service: TableActionService) => service.addTrip(order),
              isDisplayed: true,
            },
            cancel: {
              click: (_, service: TableActionService) => service.cancelOrder(order),
              isDisplayed: true,
            },
            delete: {
              click: (_, service: TableActionService) => service.removeOrderCompletely(order),
              isDisplayed: order.source !== 'internal',
            },
          },
        },
      },
    }, extendMappings);
    return this.getOrderToViewMapper(order, mappings);
  }

  public getGeneralOrderToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    let trip = null;
    if (order.orderTrips && order.orderTrips[0]) {
      trip = order.orderTrips[0].trip;
    }

    const mappings = deepmerge({
      firstRow: {
        executers: {
          driver: trip && {
            firstName: trip.driver && trip.driver.firstName || '',
            lastName: trip.driver && trip.driver.lastName || '',
            avatarUrl: trip.driver && trip.driver.avatarUrl || '',
          } || undefined,
          dispatcher: trip && {
            firstName: trip.dispatcher && trip.dispatcher.firstName || '',
            lastName: trip.dispatcher && trip.dispatcher.lastName || '',
            avatarUrl: trip.dispatcher && trip.dispatcher.avatarUrl || '',
          } || undefined,
        },
        actionMenu: {
          isVertical: true,
          config: {
            globe: {
              isDisplayed: trip && trip.driverId && this.userService.isNavigateToDriverLocationAllowed(trip.driverId),
              click: () => trip
                && trip.driverId
                && this.tableNavigationService.navigateToDriverLocation(trip.driverId),
            },
            cancel: {
              click: (_, service: TableActionService) => service.cancelOrder(order),
              isDisplayed: true,
            },
            delete: {
              click: (_, service: TableActionService) => service.removeOrderCompletely(order),
              isDisplayed: order.source !== 'internal',
            },
          },
        },
      },
    }, extendMappings);
    const data = this.getOrderToViewMapper(order, mappings);
    return data;
  }

  public getAssignedAndPickedUpToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    const mappings = deepmerge({
      firstRow: {
        actionMenu: {
          config: {
            createTrip: {
              isDisplayed: true,
              click: (_, service: TableActionService) => service.createTrip(order),
            },
            addToTrip: {
              click: (_, service: TableActionService) => service.addTrip(order),
              isDisplayed: true,
            },
          },
        },
      }
    }, extendMappings);
    const data = this.getGeneralOrderToViewMapper(order, mappings);
    return data;
  }

  public getPaidOrderViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    const mappings = deepmerge({
      secondRow: {
        orderDetails: {
          paidDate: this.dateService.toFormatUtcDate(order.invoicePaidDate),
        }
      }
    }, extendMappings);
    const data = this.getGeneralOrderToViewMapper(order, mappings);
    return data;
  }

  public getOrdersInTripToViewMapper(order: OrderDTO, config: TableConfiguration, extendMappings: TableViewMapping = {}) {
    const {
      items,
      tripId,
    } = (config.context || {}) as {
      items: OrderDTO[];
      tripId: number;
    };

    let trip: TripDTO = null;
    if (order.orderTrips && order.orderTrips[0]) {
      trip = order.orderTrips[0].trip;
    }

    const isLastItemToRemove = items.length === 1;
    const removeOrderAction = (_, service: TableActionService) => service.removeOrderFromTrip(tripId, order.id);
    const removeOrderCompletelyCompletely = (_, service: TableActionService) => service.removeOrderFromTripCompletely(tripId, order.id);

    const mappings = deepmerge({
      firstRow: {
        executers: {
          driver: trip && {
            firstName: trip.driver && trip.driver.firstName || '',
            lastName: trip.driver && trip.driver.lastName || '',
            avatarUrl: trip.driver && trip.driver.avatarUrl || '',
          } || undefined,
          dispatcher: trip && {
            firstName: trip.dispatcher && trip.dispatcher.firstName || '',
            lastName: trip.dispatcher && trip.dispatcher.lastName || '',
            avatarUrl: trip.dispatcher && trip.dispatcher.avatarUrl || '',
          } || undefined,
        },
        actionMenu: {
          isVertical: true,
          config: {
            deleteFromTrip: {
              click: isLastItemToRemove ? removeOrderCompletelyCompletely : removeOrderAction,
              isDisplayed: trip.status !== TRIP_STATUS.COMPLETED,
            },
          },
        },
      },
    }, extendMappings);
    return this.getOrderToViewMapper(order, mappings);
  }

  public getTripToViewMapper(trip: TripDTO, extendMappings: TableViewMapping = {}) {
    const {
      pickLocation,
      deliveryLocation,
      distance,
      cars,
    } = trip;


    const distanceFormatted = this.orderService.formatDistance(distance);

    const record = {
      firstRow: {
        identifier: {
          uuid: trip.id,
        },
        executers: {
          driver: trip && {
            firstName: trip.driver && trip.driver.firstName || '',
            lastName: trip.driver && trip.driver.lastName || '',
            avatarUrl: trip.driver && trip.driver.avatarUrl || '',
          } || undefined,
          dispatcher: trip.dispatcher && {
            firstName: trip.dispatcher && trip.dispatcher.firstName || '',
            lastName: trip.dispatcher && trip.dispatcher.lastName || '',
            avatarUrl: trip.dispatcher && trip.dispatcher.avatarUrl || '',
          } || undefined,
          config: {
            driver: {
              config: {
                click: (tableActionService: TableActionService) => tableActionService.assignDriver(trip)
              }
            },
            dispatcher: {
              config: {
                click: (tableActionService: TableActionService) => tableActionService.assignDispatcher(trip)
              }
            }
          }
        },
        markers: {
          distance: distanceFormatted,
          config: {
            distance: { config: { isDisplayed: !!distanceFormatted } },
          }
        },
        actionMenu: {
          model: trip,
          isVertical: true,
          config: {
            delete: {
              isDisplayed: trip.status !== TRIP_STATUS.COMPLETED,
              click: (_, service: TableActionService) => service.removeTrip(trip),
            },
          }
        }
      },
      secondRow: {
        selectRowClick: () => this.tableNavigationService.navigateToTrip(trip),
        origin: {
          location: this.orderService.fullAddress(pickLocation),
        },
        destination: {
          location: this.orderService.fullAddress(deliveryLocation),
        },
        orderDetails: {
          price: this.orderService.formatPrice(trip.totalPrice),
          orderStatus: trip.status && trip.status.toUpperCase(),
        },
        cargo: { cars: cars.map(car => ({ ...car, description: `${car.year} ${car.make} ${car.model} ${car.type}` })) },
      }
    };

    const result = deepmerge(record, extendMappings);
    return result;
  }

  public getAdminOrderToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    const {
      clientPaymentStatus,
      company,
      createdBy
    } = order;

    let trip = null;
    if (order.orderTrips && order.orderTrips[0]) {
      trip = order.orderTrips[0].trip;
    }

    const executers = {
      driver: trip && {
        firstName: trip.driver && trip.driver.firstName || '',
        lastName: trip.driver && trip.driver.lastName || '',
        avatarUrl: trip.driver && trip.driver.avatarUrl || '',
      } || undefined,
      dispatcher: trip && {
        firstName: trip.dispatcher && trip.dispatcher.firstName || '',
        lastName: trip.dispatcher && trip.dispatcher.lastName || '',
        avatarUrl: trip.dispatcher && trip.dispatcher.avatarUrl || '',
      } || undefined,
      carrier: company && {
        firstName: company.name || '',
        lastName: '',
        avatarUrl: company.avatarUrl || '',
      } || undefined,
    };

    const mappings = deepmerge({
      firstRow: {
        executers: Object.values(executers).find(x => !!x) ? executers : undefined,
        actionMenu: {
          isVertical: true,
          config: {
            globe: {
              isDisplayed: trip && trip.driverId && this.userService.isNavigateToDriverLocationAllowed(trip.driverId),
              click: () => trip
                && trip.driverId
                && this.tableNavigationService.navigateToDriverLocation(trip.driverId),
            },
            delete: {
              click: (_, tableActionService: TableActionService) => tableActionService.removeOrderCompletely(order),
              isDisplayed: true,
            }
          },
        }
      },
      secondRow: {
        orderDetails: {
          orderStatus: order.status,
          paymentStatus: clientPaymentStatus,
          createdByAdmin: order.createdByAdmin,
          config: {
            paymentStatus: {
              config: {
                isDisplayed: clientPaymentStatus && clientPaymentStatus !== CLIENT_PAYMENT_STATUSES.NONE
              }
            }
          },
        },
        client: createdBy && {
          headerName: ".Client",
          businessName: createdBy.businessName,
          personName: `${createdBy.firstName} ${createdBy.lastName}`,
          location: `${createdBy.state}, ${createdBy.city}, ${createdBy.zip}`,
          email: createdBy && createdBy.email || undefined,
          phone: createdBy.phoneNumber,
        } || {},
      }
    }, extendMappings)
    return this.getOrderToViewMapper(order, mappings);
  }

  public getAdminPublishedOrderToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    const mappings = deepmerge({
      firstRow: {
        actionMenu: {
          isVertical: true,
          config: {
            publish: {
              click: (_, tableActionService: TableActionService) => tableActionService.publish(order),
              header: `Publish ${this.orderService.formatPrice(order.salePrice)}$`,
              isDisplayed: !order.published && order.status !== ORDER_STATUS.CANCELED,
            },
            unpublish: {
              click: (_, tableActionService: TableActionService) => tableActionService.unpublish(order),
              header: `Unpublish ${this.orderService.formatPrice(order.salePrice)}$`,
              isDisplayed: order.published && order.status !== ORDER_STATUS.CANCELED,
            },
          },
        }
      },
    }, extendMappings)
    return this.getAdminOrderToViewMapper(order, mappings);
  }

  public getAdminRequestedOrderToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    const mappings = deepmerge({
      firstRow: {
        actionMenu: null,
        actions: {
          config: {
            viewDispatches: {
              config: {
                click: () => this.tableNavigationService.navigateToDispatch(order),
                isDisplayed: true,
              }
            },
            deleteOrder: {
              config: {
                click: (_, tableActionService: TableActionService) => tableActionService.removeOrderCompletely(order),
                isDisplayed: true,
              }
            },
          }
        }
      },
    }, extendMappings)
    return this.getAdminOrderToViewMapper(order, mappings);
  }

  public getReportOrderToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    let trip = null;
    if (order.orderTrips && order.orderTrips[0]) {
      trip = order.orderTrips[0].trip;
    }

    const mappings = deepmerge({
      firstRow: {
        executers: {
          driver: trip && {
            firstName: trip.driver && trip.driver.firstName || '',
            lastName: trip.driver && trip.driver.lastName || '',
            avatarUrl: trip.driver && trip.driver.avatarUrl || '',
          } || undefined,
          dispatcher: trip && {
            firstName: trip.dispatcher && trip.dispatcher.firstName || '',
            lastName: trip.dispatcher && trip.dispatcher.lastName || '',
            avatarUrl: trip.dispatcher && trip.dispatcher.avatarUrl || '',
          } || undefined,
        },
        actionMenu: {
          isVertical: true,
          config: {
            globe: {
              isDisplayed: trip && trip.driverId && this.userService.isNavigateToDriverLocationAllowed(trip.driverId),
              click: () => trip
                && trip.driverId
                && this.tableNavigationService.navigateToDriverLocation(trip.driverId),
            },
            cancel: {
              click: (_, service: TableActionService) => service.cancelOrder(order),
              isDisplayed: true,
            },
          },
        },
      },
    }, extendMappings);
    return this.getOrderToViewMapper(order, mappings);
  }

  public getDashboardOrderToViewMapper(order: OrderDTO, extendMappings: TableViewMapping = {}) {
    let trip = null;
    if (order.orderTrips && order.orderTrips[0]) {
      trip = order.orderTrips[0].trip;
    }

    const mappings = deepmerge({
      firstRow: {
        executers: {
          driver: trip && {
            firstName: trip.driver && trip.driver.firstName || '',
            lastName: trip.driver && trip.driver.lastName || '',
            avatarUrl: trip.driver && trip.driver.avatarUrl || '',
          } || undefined,
          dispatcher: trip && {
            firstName: trip.dispatcher && trip.dispatcher.firstName || '',
            lastName: trip.dispatcher && trip.dispatcher.lastName || '',
            avatarUrl: trip.dispatcher && trip.dispatcher.avatarUrl || '',
          } || undefined,
        },
        actionMenu: null,
      },
    }, extendMappings);
    return this.getOrderToViewMapper(order, mappings);
  }

  private isLate(data: Date) {
    const result = data && dayjs(data).unix() < dayjs().unix();
    return result;
  }

  private getOfferAction(quote: QuoteDTO) {
    const isCompanyAdminOrDispatcher = this.userService.isUserHasRole([ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER]);

    if (isCompanyAdminOrDispatcher) {
      return (_, service: TableActionService) => service.offerDiscountByCarrierOrDispatcher(quote);
    }

    return (_, service: TableActionService) => service.newOfferDiscount(quote.id, quote.initialPrice);
  }

  private getSuggestedPrice(quote: QuoteDTO) {
    const isAdmin = this.userService.isUserHasRole([ROLES_ENUM.SUPER_ADMIN]);

    if (isAdmin && quote.priceWithDiscount !== quote.initialPrice) {
      return quote.priceWithDiscount || quote.customPrice || undefined;
    }

    const quoteOffers = quote.quoteOffers || [];
    const lastQuoteOffer = quoteOffers.length > 0
      && quoteOffers.reduce((first, second) => first.id > second.id ? first : second)
      || undefined;

    return quote.customPrice || lastQuoteOffer && lastQuoteOffer.priceWithDiscount || undefined;
  }

  private addQuoteViewSettings(record: TableViewMapping, entity: INotificatedEntity): TableViewMapping {

    const quoteCreatedNotificaton = this.getNotificationWithType(entity, WEB_NOTIFICATION.QUOTE_CREATED);

    const updatedRecord = deepmerge(record, {
      notificationDictionary: {
        quoteCreated: quoteCreatedNotificaton
      },
    });

    return updatedRecord as TableViewMapping;
  }

  private getNotificationWithType(entity: INotificatedEntity, webNotificationType: string) {
    const result = entity.notifications && entity.notifications.find(notification => notification.type === webNotificationType);
    return result;
  }

  private getServiceFee(paymentMethod: string, order: OrderDTO) {
    if (order.createdByAdmin) {
      return undefined;
    }

    if (paymentMethod === PaymentMethodEnum.CheckAtDelivery) {
      return `${this.generalService.policy.serviceAbsoluteFeeForCarrier}$ by Carrier`;
    } else if (paymentMethod === PaymentMethodEnum.ACH) {
      return `${this.generalService.policy.serviceAbsoluteFee}$ by Client`;
    }

    return undefined;
  }

  private normalizeOptionalString(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim();
  }

  private normalizeLocation(location: OrderDTO['pickLocation']) {
    return {
      ...(location || {}),
      address: this.normalizeOptionalString(location?.address),
      city: this.normalizeOptionalString(location?.city),
      state: this.normalizeOptionalString(location?.state),
      zipCode: this.normalizeOptionalString(location?.zipCode),
    } as OrderDTO['pickLocation'];
  }

  private normalizeContact(contact: OrderDTO['sender']) {
    return {
      ...(contact || {}),
      firstName: this.normalizeOptionalString(contact?.firstName),
      lastName: this.normalizeOptionalString(contact?.lastName),
      phoneNumber: this.normalizeOptionalString(contact?.phoneNumber),
      email: this.normalizeOptionalString(contact?.email),
      businessName: this.normalizeOptionalString(contact?.businessName),
    } as OrderDTO['sender'];
  }

  private normalizeShipper(shipper: OrderDTO['shipper']) {
    return {
      ...(shipper || {}),
      companyName: this.normalizeOptionalString(shipper?.companyName),
      fullName: this.normalizeOptionalString(shipper?.fullName),
      state: this.normalizeOptionalString(shipper?.state),
      city: this.normalizeOptionalString(shipper?.city),
      zipCode: this.normalizeOptionalString(shipper?.zipCode),
      email: this.normalizeOptionalString(shipper?.email),
    } as OrderDTO['shipper'];
  }

  private normalizeCars(cars: OrderDTO['cars']) {
    if (!Array.isArray(cars)) {
      return [];
    }

    return cars.map(car => ({
      ...car,
      make: this.normalizeOptionalString(car?.make),
      model: this.normalizeOptionalString(car?.model),
      type: this.normalizeOptionalString(car?.type),
      year: this.normalizeOptionalString(car?.year),
      vin: this.normalizeOptionalString(car?.vin),
      inop: Boolean(car?.inop),
    }));
  }
}
